import type { FC } from "react";
import React from "react";
import { DebugBadge, DebugButton, DebugKeyValue } from "./debug.styles";

export type UserDebugInfo = {
  userId: string;
  refreshTokenSub: string | null;
  storage: string;
  lastActive: string;
  lastActiveTime: number;
  hasRefreshToken: boolean;
  refreshTokenValid: boolean;
  authStageInPrimaryStorage: string | null;
  authStageInOtherStorage: string | null;
  hasDataConflict: boolean;
  hasAppAccess: boolean;
  priority:
    | "app_access"
    | "fullAuth"
    | "partialAuth"
    | "anonymous"
    | "noAuth"
    | "nullAuth"
    | "invalid";
  isCurrentlyActive: boolean;
};

interface UserStorageTableProps {
  users: UserDebugInfo[];
  onMoveUser: (userId: string, fromStorage: string) => void;
  onDeleteUser: (userId: string) => void;
}

const formatTime = (timestamp: number) => {
  return new Date(timestamp).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const truncateId = (id: string | null) => {
  if (!id) return "—";
  if (id.length <= 12) return id;
  return `${id.slice(0, 6)}...${id.slice(-4)}`;
};

const UserCard: FC<{
  user: UserDebugInfo;
  onMove: () => void;
  onDelete: () => void;
}> = ({ user, onMove, onDelete }) => {
  return (
    <div
      style={{
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "0.75rem",
        marginBottom: "0.5rem",
        backgroundColor: user.isCurrentlyActive
          ? "#f0fdf4"
          : user.hasDataConflict
            ? "#fef2f2"
            : "#fff"
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "0.5rem"
        }}
      >
        <div>
          <code style={{ fontSize: "11px", fontWeight: 600 }}>
            {truncateId(user.refreshTokenSub ?? user.userId)}
          </code>
          {user.isCurrentlyActive && (
            <DebugBadge variant="success" style={{ marginLeft: "0.5rem" }}>
              Active
            </DebugBadge>
          )}
        </div>
        <DebugBadge
          variant={user.storage === "localStorage" ? "info" : "warning"}
        >
          {user.storage === "localStorage" ? "Local" : "Session"}
        </DebugBadge>
      </div>

      <DebugKeyValue style={{ padding: "0.25rem 0" }}>
        <span className="key" style={{ minWidth: "80px" }}>
          Last Active
        </span>
        <span className="value" style={{ fontSize: "12px" }}>
          {formatTime(user.lastActiveTime)}
        </span>
      </DebugKeyValue>

      <DebugKeyValue style={{ padding: "0.25rem 0" }}>
        <span className="key" style={{ minWidth: "80px" }}>
          Token
        </span>
        <span className="value">
          {user.refreshTokenValid ? (
            <DebugBadge variant="success">Valid</DebugBadge>
          ) : (
            <DebugBadge variant="danger">Invalid</DebugBadge>
          )}
        </span>
      </DebugKeyValue>

      <DebugKeyValue style={{ padding: "0.25rem 0" }}>
        <span className="key" style={{ minWidth: "80px" }}>
          Auth Stage
        </span>
        <span className="value" style={{ fontSize: "12px" }}>
          {user.authStageInPrimaryStorage || "none"}
          {user.hasDataConflict && (
            <span style={{ color: "#dc2626", marginLeft: "0.25rem" }}>
              (conflict)
            </span>
          )}
        </span>
      </DebugKeyValue>

      <DebugKeyValue style={{ padding: "0.25rem 0" }}>
        <span className="key" style={{ minWidth: "80px" }}>
          App Access
        </span>
        <span className="value">
          {user.hasAppAccess ? (
            <DebugBadge variant="success">Yes</DebugBadge>
          ) : (
            <DebugBadge variant="info">No</DebugBadge>
          )}
        </span>
      </DebugKeyValue>

      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          marginTop: "0.75rem",
          borderTop: "1px solid #e0e0e0",
          paddingTop: "0.75rem"
        }}
      >
        <DebugButton className="small" onClick={onMove} style={{ flex: 1 }}>
          Move to {user.storage === "localStorage" ? "Session" : "Local"}
        </DebugButton>
        <DebugButton
          className="small danger"
          onClick={onDelete}
          style={{ flex: 1 }}
        >
          Delete
        </DebugButton>
      </div>
    </div>
  );
};

const UserStorageTable: FC<UserStorageTableProps> = ({
  users,
  onMoveUser,
  onDeleteUser
}) => {
  if (users.length === 0) {
    return (
      <p style={{ color: "#666", fontSize: "13px", margin: "0.5rem 0" }}>
        No users found in storage
      </p>
    );
  }

  return (
    <div style={{ marginTop: "0.75rem" }}>
      {users.map((user, index) => (
        <UserCard
          key={index}
          user={user}
          onMove={() => onMoveUser(user.userId, user.storage)}
          onDelete={() => onDeleteUser(user.userId)}
        />
      ))}
    </div>
  );
};

export default UserStorageTable;
